/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import {
  type GetProductListResultsUsingGetParamsModel,
  type ProductShelfResponseModel,
} from '@/api/types/browse-search-types';
import { requestBaseURL } from '@/config/serviceAPI';
import { type ProductShelfResponse } from '../interface';
import { type AxiosInstance, isAxiosError } from 'axios';
import { type QueryClient } from '@tanstack/react-query';
import { getAxios } from '@/lib/axios';
import { parseUrl, stringifyUrl } from '@/utils/urlHelpers';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useAppState } from '@/stores/AppState';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { usePageType } from './getPageType';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { useContentStackPLPData } from '@/features/productListingPage/api/getContentStackProductListingPageData';
import logger from '@/utils/logger';
import {
  type BaseInfiniteMergeFunction,
  type BaseMergedData,
  createInfiniteQuery,
} from '@/utils/query/createInfiniteQuery';
import { routePaths } from '@/constants/routePaths';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useQueryResultOnSuccess } from '@/hooks/query/useQueryResultOnSuccess';
import { useQueryResultOnError } from '@/hooks/query/useQueryResultOnError';
import { pageTypes } from '@/constants/page';
import { useRetryConfigForCategory } from '@/components/RetryConfigContext';
import { isServer } from '@/utils/isServer';
import { useRouter } from 'next/navigation';
import { useDeviceType } from '@/utils/useDeviceType';
import { getShelfRequestStoreId } from '@/utils/getShelfRequestStoreId';

const CATEGORY_SHELF_URL = `${requestBaseURL}/external/product-discovery/browse-search/v1/product-shelves`;

type Options = {
  locale?: Locale;
  vehicleId: string | undefined;
  taxonomyPath?: string;
  tag?: string;
  canonicalPath?: string;
  pageType?: string;
  botEnabledFacetPath?: string;
  path?: string | undefined;
  recsPerPage?: string | undefined;
  makeModelYearPath?: string | undefined;
  storeId?: string | undefined;
  preview: boolean;
  rewardsId: string;
  eCookieId: string;
  ignoreVehicleSpecificProductsCheck?: boolean;
};

const getProductShelfResults = async (
  options: Options,
  pageNumber: number,
  axiosInstance?: AxiosInstance,
) => {
  const {
    eCookieId,
    locale,
    taxonomyPath,
    vehicleId,
    canonicalPath,
    preview,
    path,
    recsPerPage,
    botEnabledFacetPath,
    makeModelYearPath,
    rewardsId,
    tag,
    storeId,
    ignoreVehicleSpecificProductsCheck,
    pageType,
  } = options;

  if (!canonicalPath) {
    throw new Error('canonicalPath must be provided in getCategoryShelfResults');
  }

  const country = getCountryFromLocale(locale || 'en-US');
  const customerType = 'B2C';
  const salesChannel = 'ECOMM';
  const parsedUrl = parseUrl(path ?? '');
  const isClearanceShelf = path?.startsWith(`${routePaths.clearance}`);

  const shouldAddIgnoreVehicleParam =
    pageType === pageTypes.ProductShelf &&
    !!vehicleId &&
    ignoreVehicleSpecificProductsCheck === undefined;

  const ignoreVehicleSpecificProductsCheckOverride = shouldAddIgnoreVehicleParam
    ? false
    : ignoreVehicleSpecificProductsCheck;

  const params: GetProductListResultsUsingGetParamsModel = {
    country,
    customerType,
    salesChannel,
    preview,
    canonicalPath,
    pageNumber,
    recordsPerPage: recsPerPage
      ? Number(recsPerPage)
      : parsedUrl.query.recsPerPage
        ? Number(parsedUrl.query.recsPerPage)
        : undefined,
    sort: parsedUrl.query.sort,
    vehicleId,
    storeId,
    ...(tag && {
      ruleTags: tag,
      isLandingPage: true,
      facet: parsedUrl.query.facet ?? undefined,
    }),
    ignoreVehicleSpecificProductsCheck: isClearanceShelf,
    ...(!tag && {
      taxonomyPath,
      botEnabledFacetPath,
      searchedKeyword: !parsedUrl.query.redirectUrl
        ? (parsedUrl.query.filterByKeyWord ?? parsedUrl.query.searchText)
        : undefined,
      makeModelYearPath: makeModelYearPath === '' ? undefined : makeModelYearPath,
      facet: parsedUrl.query.facet ?? undefined,
      partNumberSearch: Boolean(parsedUrl.query.partNumberSearch),
      minPrice: parsedUrl.query.minPrice,
      maxPrice: parsedUrl.query.maxPrice,
      ...(ignoreVehicleSpecificProductsCheckOverride !== undefined && {
        ignoreVehicleSpecificProductsCheck: ignoreVehicleSpecificProductsCheckOverride,
      }),
    }),
  };

  const response = await getAxios(axiosInstance).get<ProductShelfResponseModel>(
    CATEGORY_SHELF_URL,
    {
      ...(country === 'USA' && {
        headers: {
          eCookieId,
          ...(!!rewardsId && { rewardsId }),
        },
      }),
      params,
    },
  );
  return productShelfResultsSelector(response.data);
};

const productShelfResultsSelector = ({
  productShelfResults,
  redirectUrl,
  xFusionQueryId,
}: ProductShelfResponseModel): ProductShelfResponse => {
  return {
    productShelfResults,
    redirectLocation: redirectUrl ?? '',
    xFusionQueryId,
  };
};

const selectInfiniteQueryData: BaseInfiniteMergeFunction<ProductShelfResponse, number> = (data) => {
  const mergedData = data?.pages?.reduce<ProductShelfResponse>(
    (acc, page) => {
      const { redirectPageType, redirectUrl, xFusionQueryId } = page;
      if (page.productShelfResults) {
        return {
          ...acc,
          productShelfResults: {
            ...acc.productShelfResults,
            ...page.productShelfResults,
            skuRecords: [
              ...(acc.productShelfResults?.skuRecords ?? []),
              ...(page.productShelfResults.skuRecords ?? []),
            ],
          },
          redirectPageType,
          redirectUrl,
          xFusionQueryId,
        };
      }
      return acc;
    },
    { productShelfResults: { skuRecords: [] } } as unknown as ProductShelfResponse,
  );

  return {
    mergedData,
    pages: data.pages,
    pageParams: data.pageParams,
    pageNumber: data.pages.length,
  };
};

const {
  useData: useProductShelfResultsData,
  query: productsShelfQuery,
  prefetch: prefetchProductShelfResultsData,
  getFromCache,
} = createInfiniteQuery<
  ProductShelfResponse,
  Options,
  number,
  BaseMergedData<ProductShelfResponse, number>
>(['productshelf-results'], getProductShelfResults, {
  initialPageParam: 1,
  getNextPageParam: (lastPage, allPages) => {
    const totalRecords = lastPage?.productShelfResults?.totalNumberOfRecords ?? 0;
    const currentPageNumber = allPages.length;

    // Count actual records loaded across all pages
    const loadedRecords = allPages.reduce((sum, page) => {
      return sum + (page.productShelfResults?.skuRecords?.length ?? 0);
    }, 0);

    // Only fetch next page if we haven't loaded all records yet
    if (loadedRecords < totalRecords) {
      return currentPageNumber + 1;
    }
    return undefined;
  },
  selectInfiniteQueryData,
});

export const useProductShelfResults = ({
  enabled = true,
  ...rest
}: {
  enabled?: boolean;
} = {}) => {
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const headerResult = useHeaderData();
  const isBot = useDeviceType() === 'bot';
  const preferredVehicle = getPreferredVehicle(headerResult.data);
  const locale = useLocale();
  const appState = useAppState();
  const isMerchShelfPage = legacyRouter.route === '/deals/[cmsPath]/[[...slug]]';
  const { data: plpCMSData, isSuccess: isPLPCMSDataSuccess } = useContentStackPLPData({
    enabled: true,
  });
  const retryConfig = useRetryConfigForCategory('SEARCH');
  // TODO: replace with router.asPath when Next bug is fixed
  // https://github.com/vercel/next.js/issues/46876
  const reqUrl = isServer ? appState.state.reqUrl : legacyRouter.asPath;

  if (reqUrl == null) {
    throw new Error(`A 'path' option is required`);
  }
  const { acesEnabled } = useAcesFlags();
  const { data: pageTypeData } = usePageType();
  const parsedUrl = parseUrl(reqUrl);

  const lwTag = plpCMSData?.additional_attributes_merch_shelf_page?.lucidworks_fusion_tag
    ? plpCMSData.additional_attributes_merch_shelf_page.lucidworks_fusion_tag
    : undefined;

  const taxonomyPath = pageTypeData?.taxonomyPath || undefined;
  delete parsedUrl.query.loadMore;
  const cleanedReqUrl = stringifyUrl(parsedUrl);

  const queryOptions: Options = {
    rewardsId: headerResult.data?.myAccountMap?.rewardsId ?? '',
    eCookieId: appState.state.eCookieId,
    locale,
    vehicleId: preferredVehicle?.catalogVehicleId,
    pageType: pageTypeData?.pageType || undefined,
    canonicalPath: pageTypeData?.canonicalPath || parsedUrl.url,
    taxonomyPath,
    tag: lwTag,
    botEnabledFacetPath: pageTypeData?.botEnabledFacetPath || undefined,
    makeModelYearPath: pageTypeData?.makeModelYearPath || undefined,
    recsPerPage: '24',
    storeId: getShelfRequestStoreId(
      headerResult.data?.storeNumber,
      isBot,
      pageTypeData?.botEnabledFacetPath,
    ),
    path: legacyRouter.route === '/cms/taxonomy-selector' ? taxonomyPath : cleanedReqUrl,
    preview: showXMPreviewDate(),
    ignoreVehicleSpecificProductsCheck:
      typeof parsedUrl.query.ignoreVehicleSpecificProductsCheck === 'string'
        ? parsedUrl.query.ignoreVehicleSpecificProductsCheck === 'true'
        : undefined,
    ...rest,
  };
  const infiniteQueryResult = useProductShelfResultsData({
    queryOptions,
    enabled: isMerchShelfPage
      ? isPLPCMSDataSuccess && !!lwTag && !acesEnabled
      : enabled &&
        !acesEnabled &&
        pageTypeData?.pageType === 'ProductShelf' &&
        (legacyRouter.route === '/[...seoUrlPath]' ||
          legacyRouter.route === '/cms/taxonomy-selector'),
    staleTime: 30 * 60000,
    ...retryConfig,
  });

  useQueryResultOnSuccess(
    infiniteQueryResult,
    (data) => {
      if (data.mergedData?.redirectLocation) {
        router.replace(data.mergedData.redirectLocation);
      }
    },
    true,
  );

  useQueryResultOnError(infiniteQueryResult, (err) => {
    logger.error({
      message: 'Error fetching product shelf results',
      meta: {
        error: isAxiosError(err) ? err.response : err,
      },
    });

    try {
      router.replace('/errorPage');
    } catch {
      throw new Error(`There was a problem redirecting to /errorPage`);
    }
  });

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetching,
    isPending,
    status,
    refetch,
    isFetchingNextPage,
  } = infiniteQueryResult;

  const { mergedData, pages, pageNumber = 1 } = infiniteData ?? {};

  return {
    pages,
    data: mergedData,
    pageNumber,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetching,
    isPending,
    status,
    refetch,
    isFetchingNextPage,
  };
};

export const prefetchProductShelfResults = async (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  options: Options,
) => {
  return prefetchProductShelfResultsData(queryClient, options, axiosInstance);
};

export const prefetchProductShelfResultsInfinite = async (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  options: Options,
) => {
  return prefetchProductShelfResultsData(queryClient, options, axiosInstance);
};

export const getProductShelfResultsFromCache = (queryClient: QueryClient, options: Options) => {
  return getFromCache(queryClient, options);
};

export const useProduleShelfFacets = () => {
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const headerResult = useHeaderData();
  const isBot = useDeviceType() === 'bot';
  const locale = useLocale();
  const appState = useAppState();

  // TODO: replace with router.asPath when Next bug is fixed
  // https://github.com/vercel/next.js/issues/46876
  const reqUrl = isServer ? appState.state.reqUrl : legacyRouter.asPath;

  const parsedUrl = reqUrl ? parseUrl(reqUrl) : null;
  const cleanedReqUrl = parsedUrl ? stringifyUrl(parsedUrl) : undefined;

  if (reqUrl == null) {
    throw new Error(`A 'path' option is required`);
  }

  const { data: pageTypeData } = usePageType();

  const queryResult = useProductShelfResultsData({
    queryOptions: {
      rewardsId: headerResult.data?.myAccountMap?.rewardsId ?? '',
      eCookieId: appState.state.eCookieId,
      locale,
      vehicleId: undefined,
      pageType: pageTypeData?.pageType ?? 'ProductShelf',
      canonicalPath: pageTypeData?.canonicalPath ?? parsedUrl?.url,
      taxonomyPath: pageTypeData?.taxonomyPath,
      botEnabledFacetPath: undefined,
      makeModelYearPath: undefined,
      recsPerPage: '1',
      storeId: getShelfRequestStoreId(
        headerResult.data?.storeNumber,
        isBot,
        pageTypeData?.botEnabledFacetPath,
      ),
      path: cleanedReqUrl,
      preview: showXMPreviewDate(),
    },
    staleTime: 30 * 60000,
  });

  useQueryResultOnSuccess(
    queryResult,
    (data) => {
      if (data.mergedData?.redirectLocation) {
        router.replace(data.mergedData.redirectLocation);
      }
    },
    true,
  );

  const { data, isPending, isSuccess, isFetching } = queryResult;

  return {
    data: data?.pages[0],
    isFetching,
    isPending,
    isSuccess,
  };
};

export { productsShelfQuery, useProductShelfResultsData };
